import { Observable, type ReadonlyObservable } from "@/lib/studio/observable";
import { MAX_HOLD_MS } from "../media-sync/frame-schedule";
import type { AudioOutput } from "./audio-types";

/**
 * Les sons reçus, rendus par WebAudio.
 *
 * # Pourquoi WebAudio
 *
 * Le retard qui cale un son sur les notes se pose ici, après le décodage : pas
 * avant, où NetEq le prendrait pour de la gigue (voir
 * `lib/live/media-sync/media-sync.ts`). Un `DelayNode` le tient à l'échantillon
 * près, jusqu'à la plus longue retenue d'un spectateur. Et joué dans le contexte
 * du studio, le son sort avec le même tampon que les notes : ce que `latencyMs`
 * retranche vaut pour les deux.
 *
 * # Un élément muet par flux
 *
 * Sous Chromium, une piste reçue ne s'écoule que si un élément média la lit :
 * sans lui, le récepteur jette ses paquets et WebAudio n'entend rien (vérifié
 * sur Chromium 148). Un élément muet la lit donc ; c'est WebAudio qu'on entend.
 *
 * # Changer de retard sans claquer
 *
 * Deux lignes à retard par flux, qui tournent toutes les deux. Un nouveau retard
 * se pose sur celle qu'on n'entend pas, et l'on passe de l'une à l'autre en
 * fondu : ni saut, ni son étiré. Mesuré sous Chromium 148 : de 400 ms à 4,3 s,
 * le son sort à sa retenue dès le fondu suivant, sans rien que le tampon de
 * réception ait à combler.
 */

/**
 * En deçà, en ms, un retard ne bouge pas : c'est ce que la mesure bouge d'elle-même
 * d'un relevé à l'autre, et un fondu pour si peu ne s'entendrait pas mieux.
 */
export const DELAY_STEP_MS = 5;

/**
 * La durée d'un fondu d'un retard à l'autre, en secondes : assez court pour ne
 * rien entendre glisser, assez long pour ne rien entendre claquer.
 */
export const CROSSFADE_SECONDS = 0.05;

/** Le plus long retard qu'une ligne tient, en secondes : la plus longue retenue, et de la marge. */
const MAX_DELAY_SECONDS = MAX_HOLD_MS / 1000 + 1;

/** Le temps que met un volume à rejoindre sa valeur, en secondes : de quoi ne pas claquer non plus. */
const VOLUME_TIME_CONSTANT_SECONDS = 0.015;

export interface WebAudioOutputOptions {
  /**
   * Le contexte où jouer : celui du studio, pour que la voix sorte avec le même
   * tampon que les notes. Sinon, un contexte ouvert au premier son, et fermé
   * avec la sortie.
   */
  context?: () => AudioContext;
  /**
   * Ce que la sortie ajoute avant l'oreille, en ms : la valeur que le studio
   * retranche de ses notes. Lue sur le contexte sinon.
   */
  latencyMs?: () => number;
}

/** Faut-il déplacer un retard tenu vers celui qu'on vise ? */
export function shouldMoveDelay(heldMs: number, targetMs: number): boolean {
  return Math.abs(targetMs - heldMs) >= DELAY_STEP_MS;
}

interface DelayLine {
  delay: DelayNode;
  level: GainNode;
}

interface Voice {
  element: HTMLAudioElement;
  source: MediaStreamAudioSourceNode;
  lines: readonly [DelayLine, DelayLine];
  /** La ligne qu'on entend. */
  heard: 0 | 1;
  delayMs: number;
  volume: GainNode;
  /** La fin du fondu en cours, sur l'horloge du contexte. */
  fadeEndsAt: number;
}

export class WebAudioOutput implements AudioOutput {
  private readonly voices = new Map<string, Voice>();
  private readonly _blocked = new Observable<boolean>(false);
  private context: AudioContext | null = null;
  private ownsContext = false;
  private readonly onStateChange = () => this.readState();

  constructor(private readonly options: WebAudioOutputOptions = {}) {}

  get blocked(): ReadonlyObservable<boolean> {
    return this._blocked;
  }

  play(id: string, stream: MediaStream, delayMs: number, volume: number): void {
    this.stop(id);

    const context = this.openContext();
    const element = new Audio();

    // Muet : il ne sert qu'à faire couler la piste. Voir « Un élément muet par flux ».
    element.muted = true;
    element.srcObject = stream;
    void element.play().catch(() => undefined);

    const source = context.createMediaStreamSource(stream);
    const gain = context.createGain();

    gain.gain.value = clampVolume(volume);
    gain.connect(context.destination);

    const heard = delayLine(context, source, gain, 1);
    const silent = delayLine(context, source, gain, 0);

    heard.delay.delayTime.value = toDelaySeconds(delayMs);
    this.voices.set(id, { element, source, lines: [heard, silent], heard: 0, delayMs, volume: gain, fadeEndsAt: 0 });
    this.readState();
  }

  setDelay(id: string, delayMs: number): void {
    const voice = this.voices.get(id);

    if (voice === undefined || !shouldMoveDelay(voice.delayMs, delayMs)) {
      return;
    }

    const now = voice.source.context.currentTime;

    // Pendant un fondu, la ligne qui s'éteint sonne encore : changer son retard y
    // ferait un saut. La cadence suivante reprendra la demande.
    if (now < voice.fadeEndsAt) {
      return;
    }

    const next = voice.heard === 0 ? 1 : 0;
    const from = voice.lines[voice.heard];
    const to = voice.lines[next];

    // La ligne qu'on n'entend pas prend le nouveau retard : elle tourne depuis le
    // début du flux, et a déjà en mémoire ce qu'il faut faire entendre.
    to.delay.delayTime.setValueAtTime(toDelaySeconds(delayMs), now);
    fadeTo(from.level.gain, 0, now);
    fadeTo(to.level.gain, 1, now);
    voice.heard = next;
    voice.delayMs = delayMs;
    voice.fadeEndsAt = now + CROSSFADE_SECONDS;
  }

  setVolume(id: string, volume: number): void {
    const voice = this.voices.get(id);

    if (voice === undefined) {
      return;
    }

    voice.volume.gain.setTargetAtTime(
      clampVolume(volume),
      voice.source.context.currentTime,
      VOLUME_TIME_CONSTANT_SECONDS,
    );
  }

  stop(id: string): void {
    const voice = this.voices.get(id);

    if (voice === undefined) {
      return;
    }

    this.voices.delete(id);
    voice.source.disconnect();

    for (const line of voice.lines) {
      line.delay.disconnect();
      line.level.disconnect();
    }

    voice.volume.disconnect();
    voice.element.pause();
    voice.element.srcObject = null;
    this.readState();
  }

  latencyMs(): number {
    if (this.options.latencyMs !== undefined) {
      return this.options.latencyMs();
    }

    const context = this.context;

    if (context === null) {
      return 0;
    }

    return (context.baseLatency + (context.outputLatency ?? 0)) * 1000;
  }

  async resume(): Promise<void> {
    const context = this.context;

    if (context !== null && context.state === "suspended") {
      await context.resume().catch(() => undefined);
    }

    for (const voice of this.voices.values()) {
      void voice.element.play().catch(() => undefined);
    }

    this.readState();
  }

  dispose(): void {
    for (const id of Array.from(this.voices.keys())) {
      this.stop(id);
    }

    const context = this.context;

    this.context = null;
    context?.removeEventListener("statechange", this.onStateChange);

    // Un contexte prêté — celui du studio — n'est pas à nous : il reste ouvert.
    if (context !== null && this.ownsContext) {
      void context.close().catch(() => undefined);
    }

    this._blocked.dispose();
  }

  private openContext(): AudioContext {
    if (this.context !== null) {
      return this.context;
    }

    const lent = this.options.context?.();
    const context = lent ?? new AudioContext({ latencyHint: "interactive" });

    this.context = context;
    this.ownsContext = lent === undefined;
    context.addEventListener("statechange", this.onStateChange);

    return context;
  }

  /** Retenu tant qu'un son devrait jouer dans un contexte que le navigateur tient suspendu. */
  private readState(): void {
    const context = this.context;

    this._blocked.set(this.voices.size > 0 && context !== null && context.state === "suspended");
  }
}

/** Une ligne à retard entre la source et le volume, au niveau donné. */
function delayLine(context: BaseAudioContext, source: AudioNode, output: AudioNode, level: number): DelayLine {
  const delay = context.createDelay(MAX_DELAY_SECONDS);
  const gain = context.createGain();

  gain.gain.value = level;
  source.connect(delay);
  delay.connect(gain);
  gain.connect(output);

  return { delay, level: gain };
}

/** Mène un niveau vers une valeur, depuis celui qu'il a à l'instant, où qu'en soit une rampe précédente. */
function fadeTo(param: AudioParam, value: number, now: number): void {
  const holding = param as AudioParam & { cancelAndHoldAtTime?: (time: number) => AudioParam };

  if (typeof holding.cancelAndHoldAtTime === "function") {
    holding.cancelAndHoldAtTime(now);
  } else {
    param.cancelScheduledValues(now);
    param.setValueAtTime(param.value, now);
  }

  param.linearRampToValueAtTime(value, now + CROSSFADE_SECONDS);
}

function toDelaySeconds(delayMs: number): number {
  return Math.min(MAX_HOLD_MS, Math.max(0, delayMs)) / 1000;
}

function clampVolume(volume: number): number {
  return Math.min(1, Math.max(0, volume));
}
